import React, { useEffect, useRef, useState } from 'react';
import '../../../components/modal/add-car/AddCarModal.css';
import './FornecedorDetalhesModal.css';
import '../../../shared/ui/modal-close-button.css';

/**
 * Quais itens um fornecedor recebe (portal-cotacoes/at-18, reunião com Compras de 29/10/2026).
 *
 * Fornecedor especializado (suspensão, vidro) não responde orçamento inteiro: o comprador manda a
 * ele só as peças dele. Usado no passo de fornecedores da nova cotação e no "Convidar fornecedor".
 *
 * A escolha é um rascunho local: só sai no "Confirmar"; "Cancelar" desfaz. Abre SOBRE outro modal
 * (ou sobre o wizard), por isso o overlay é `--empilhado` (BUG-R97) e quem o hospeda trava o próprio
 * "clicou fora" enquanto ele está aberto.
 */

export interface ItemEscolhivel {
  id: string;
  descricao: string;
  /** Texto curto ao lado da descrição (quantidade e unidade). */
  detalhe?: string;
}

interface EscolhaDeItensModalProps {
  titulo: string;
  itens: ItemEscolhivel[];
  marcados: string[];
  onConfirmar: (ids: string[]) => void;
  onFechar: () => void;
}

const EscolhaDeItensModal: React.FC<EscolhaDeItensModalProps> = ({ titulo, itens, marcados, onConfirmar, onFechar }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [escolha, setEscolha] = useState<string[]>(marcados);

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (modalRef.current?.contains(e.target as Node)) return;
      onFechar();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Fecha só este modal: o Escape não pode derrubar o hospedeiro junto.
      e.stopImmediatePropagation();
      onFechar();
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc, true);
    };
  }, [onFechar]);

  const alternar = (id: string) =>
    setEscolha((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));

  return (
    <div className="add-car-modal-overlay modal-overlay--empilhado">
      <div className="fornecedor-modal-content" ref={modalRef} role="dialog" aria-label={titulo}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>{titulo}</h2>
            <span className="fornecedor-modal-subtitulo">
              {escolha.length} de {itens.length} {itens.length === 1 ? 'item' : 'itens'}
            </span>
          </div>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          <p className="fornecedor-modal-hint" style={{ marginTop: 0 }}>
            O fornecedor vê e cota só os itens marcados. Os outros nem aparecem para ele.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button type="button" className="fornecedor-modal-btn" onClick={() => setEscolha(itens.map((i) => i.id))}>
              Marcar todos
            </button>
            <button type="button" className="fornecedor-modal-btn" onClick={() => setEscolha([])}>
              Desmarcar todos
            </button>
          </div>
          <div className="fornecedor-contatos-lista" style={{ maxHeight: '50vh', overflowY: 'auto', marginBottom: 0, paddingRight: 14 }}>
            {itens.map((item) => (
              <label key={item.id} className="fornecedor-contato-linha" style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <input
                    type="checkbox"
                    checked={escolha.includes(item.id)}
                    onChange={() => alternar(item.id)}
                    style={{ accentColor: '#ea5d0e', width: 16, height: 16, flexShrink: 0 }}
                  />
                  <div className="fornecedor-contato-dados">
                    <span
                      className="fornecedor-contato-nome"
                      title={item.descricao}
                      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {item.descricao}
                    </span>
                    {item.detalhe && <span className="fornecedor-contato-canais">{item.detalhe}</span>}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="fornecedor-modal-acoes" style={{ marginTop: 16 }}>
            <button type="button" className="fornecedor-modal-btn" onClick={onFechar}>
              Cancelar
            </button>
            <button
              type="button"
              className="fornecedor-modal-btn fornecedor-modal-btn--primario"
              disabled={escolha.length === 0}
              title={escolha.length === 0 ? 'Marque ao menos um item.' : undefined}
              // Mantém a ordem da cotação, não a ordem dos cliques.
              onClick={() => onConfirmar(itens.filter((i) => escolha.includes(i.id)).map((i) => i.id))}
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EscolhaDeItensModal;
